// src/components/GuestDetailsPage.js (Upgraded Look and Full Data Display)
import React, { useState, useEffect } from 'react'; 
import { useParams, Link } from 'react-router-dom';
import { db } from '../firebase'; 
import { doc, getDoc } from 'firebase/firestore'; 

export default function GuestDetailsPage() {
    const { guestId } = useParams();
    const [guestData, setGuestData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Helper function to format Firebase Timestamps
    const formatTimestamp = (timestamp, includeTime = false) => {
        if (timestamp && typeof timestamp.toDate === 'function') {
            const options = includeTime ? { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' } : { year: 'numeric', month: 'short', day: 'numeric' };
            return timestamp.toDate().toLocaleDateString('en-US', options);
        }
        return 'N/A';
    };

    // Helper function to safely parse and format currency
    const formatCurrency = (value) => {
        const num = parseFloat(value) || 0;
        return `$${num.toFixed(2)}`;
    };
    
    // Calculates commission for display (note: fetching hotel rates here is safer in production)
    const calculateCommission = (data) => {
        const rawUpsells = parseFloat(data.addOnsPrice) || 0; 
        const totalFinalPrice = parseFloat(data.adjustedFinalPrice) || 0; 
        const taxCollected = parseFloat(data.taxCollected) || 0; 
        const depositCollected = parseFloat(data.depositCollected) || 0;
        
        const basePriceForCommission = totalFinalPrice - rawUpsells - taxCollected - depositCollected;
        const basePrice = Math.max(0, basePriceForCommission); 
        const upsells = Math.max(0, rawUpsells); 
        
        // Using representative rates as the admin dashboard now handles fetching the specific hotel rates
        const DIRECT_RATE = 0.08; 
        const OTA_RATE = 0.02; 
        const UPSELL_RATE = 0.50;

        const source = data.bookingSource || 'Direct';
        let baseRate = (source === 'OTA') ? OTA_RATE : DIRECT_RATE;
        let baseCommission = basePrice * baseRate;
        
        const upsellCommission = upsells * UPSELL_RATE;
        
        return {
            total: formatCurrency(baseCommission + upsellCommission),
            rate: (baseRate * 100).toFixed(1) + '% (Base) + ' + (UPSELL_RATE * 100).toFixed(0) + '% (Upsell)'
        };
    };

    useEffect(() => {
        const fetchGuest = async () => {
            if (!guestId) return;
            setLoading(true);
            setError('');
            try {
                const docRef = doc(db, 'guests', guestId);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    setGuestData(docSnap.data());
                } else {
                    setError("No booking found with this ID.");
                }
            } catch (err) {
                console.error("Error fetching guest data:", err);
                setError("Failed to load booking details.");
            }
            setLoading(false);
        };
        fetchGuest();
    }, [guestId]);

    if (loading) return (
        <div className="container" style={{ paddingTop: '50px' }}>
            <div className="card">Loading booking details...</div>
        </div>
    );
    
    if (error) return (
        <div className="container" style={{ paddingTop: '50px' }}>
            <div className="card error-message" style={{ backgroundColor: '#f8d7da', color: '#721c24' }}>{error}</div>
        </div>
    );

    if (!guestData) return null;

    const commissionDetails = calculateCommission(guestData);
    const roomBasePrice = parseFloat(guestData.adjustedFinalPrice || 0) - parseFloat(guestData.addOnsPrice || 0);

    return (
        <div className="container" style={{ paddingTop: '50px' }}>
            <div className="card" style={{ padding: '40px', maxWidth: '900px', margin: 'auto' }}>
                <Link to="/admin-dashboard" style={{ color: '#007bff', textDecoration: 'none', marginBottom: '20px', display: 'inline-block' }}>
                    &larr; Back to Admin Dashboard
                </Link>

                <h1 style={{ color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                    Booking Details: {guestData.guestName || 'Unnamed Guest'}
                </h1>
                <p style={{ color: '#666', marginBottom: '30px' }}>**Booking ID:** {guestId}</p>

                {/* 1. STATUS CARD */}
                <div style={{ backgroundColor: '#f0faff', borderLeft: '5px solid #007bff', padding: '15px', borderRadius: '5px', marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, color: '#007bff' }}>Booking Status</h3>
                    <span style={{ fontSize: '1.5em', fontWeight: 'bold', color: guestData.status === 'checkedOut' ? '#dc3545' : '#28a745' }}>
                        {guestData.status ? guestData.status.toUpperCase() : 'UNKNOWN'}
                    </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
                    
                    {/* COLUMN 1: BOOKING & GUEST INFO */}
                    <div>
                        <h3 style={{ color: '#333', marginBottom: '15px', borderBottom: '1px solid #eee' }}>Guest & Room Information</h3>
                        
                        <DetailItem label="Guest Name" value={guestData.guestName || 'N/A'} />
                        <DetailItem label="Contact Email" value={guestData.email || 'N/A'} />
                        <DetailItem label="Hotel Assigned" value={guestData.hotelName || 'N/A'} highlight={true} />
                        <DetailItem label="Room Number" value={guestData.roomNumber || 'N/A'} highlight={true} />
                        <DetailItem label="Booking Source" value={guestData.bookingSource || 'Direct'} />
                        <DetailItem label="Nights Booked" value={guestData.nights || '0'} />
                        
                        <h3 style={{ color: '#333', marginTop: '30px', marginBottom: '15px', borderBottom: '1px solid #eee' }}>Dates</h3>
                        <DetailItem label="Check-in Date" value={formatTimestamp(guestData.checkInDate)} />
                        <DetailItem label="Check-out Date" value={formatTimestamp(guestData.checkOutDate)} />
                        
                    </div>

                    {/* COLUMN 2: FINANCIAL & COMMISSION */}
                    <div>
                        <h3 style={{ color: '#333', marginBottom: '15px', borderBottom: '1px solid #eee' }}>Financial Summary</h3>
                        
                        <DetailItem label="Room Base Price (Rate)" value={formatCurrency(roomBasePrice)} />
                        <DetailItem label="Add-ons / Upsells" value={formatCurrency(guestData.addOnsPrice || 0)} />
                        <DetailItem label="Tax Collected" value={formatCurrency(guestData.taxCollected || 0)} />
                        <DetailItem label="Deposit Collected" value={formatCurrency(guestData.depositCollected || 0)} />
                        
                        <div style={{ borderTop: '1px dashed #ccc', margin: '10px 0', paddingTop: '10px' }}>
                            <DetailItem label="Total Final Price" value={formatCurrency(guestData.adjustedFinalPrice)} highlight={true} size="large" />
                        </div>
                        
                        <h3 style={{ color: '#28a745', marginTop: '30px', marginBottom: '15px', borderBottom: '1px solid #eee' }}>Platform Commission</h3>
                        
                        <DetailItem label="Calculated Commission" value={commissionDetails.total} highlight={true} size="large" color="#28a745" />
                        <DetailItem label="Applied Rate(s)" value={commissionDetails.rate} size="small" />
                        
                    </div>
                </div>

                {/* 3. STAFF NOTES (Full Width) */}
                <div style={{ marginTop: '30px', borderTop: '1px solid #eee', paddingTop: '30px' }}>
                    <h3 style={{ color: '#333', marginBottom: '15px' }}>Operational Notes</h3>
                    <p style={{ padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '5px', border: '1px solid #eee', minHeight: '50px' }}>
                        {guestData.staffNotes || 'No operational notes recorded.'}
                    </p>
                    <small style={{ color: '#999', float: 'right' }}>Booking created: {formatTimestamp(guestData.createdAt, true)}</small>
                </div>

            </div>
        </div>
    );
}

// Sub-component for clean display
const DetailItem = ({ label, value, highlight = false, size = 'normal', color = '#333' }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dotted #eee' }}>
        <span style={{ fontWeight: highlight ? 'bold' : 'normal', color: highlight ? color : '#555' }}>
            {label}
        </span>
        <span style={{ fontWeight: highlight ? 'bold' : 'normal', fontSize: size === 'large' ? '1.1em' : '1em', color: color }}>
            {value}
        </span>
    </div>
);